import type {
  BusinessType,
  CreateBusinessTypeRequest,
  UpdateBusinessTypeRequest,
} from "@hellocashflow/domain";

import { apiSlice } from "../store/api/baseApi";

export const ADMIN_BUSINESS_TYPES_PATH = "/admin/business-types";
export const adminBusinessTypePath = (id: string): string =>
  `${ADMIN_BUSINESS_TYPES_PATH}/${id}`;
export const archiveBusinessTypePath = (id: string): string =>
  `${adminBusinessTypePath(id)}/archive`;
export const unarchiveBusinessTypePath = (id: string): string =>
  `${adminBusinessTypePath(id)}/unarchive`;

const businessTypesSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getBusinessTypes: builder.query<BusinessType[], void>({
      query: () => ({
        url: ADMIN_BUSINESS_TYPES_PATH,
      }),
      providesTags: (result) => {
        if (!result) {
          return [{ type: "BusinessType", id: "LIST" }];
        }

        return [
          { type: "BusinessType", id: "LIST" },
          ...result.map((item) => ({
            type: "BusinessType" as const,
            id: item.id,
          })),
        ];
      },
    }),
    createBusinessType: builder.mutation<
      BusinessType,
      CreateBusinessTypeRequest
    >({
      query: (payload) => ({
        url: ADMIN_BUSINESS_TYPES_PATH,
        method: "POST",
        data: payload,
      }),
      invalidatesTags: [{ type: "BusinessType", id: "LIST" }],
    }),
    updateBusinessType: builder.mutation<
      BusinessType,
      { id: string; payload: UpdateBusinessTypeRequest }
    >({
      query: ({ id, payload }) => ({
        url: adminBusinessTypePath(id),
        method: "PUT",
        data: payload,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "BusinessType", id: args.id },
        { type: "BusinessType", id: "LIST" },
      ],
    }),
    archiveBusinessType: builder.mutation<BusinessType, string>({
      query: (id) => ({
        url: archiveBusinessTypePath(id),
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "BusinessType", id },
        { type: "BusinessType", id: "LIST" },
      ],
    }),
    unarchiveBusinessType: builder.mutation<BusinessType, string>({
      query: (id) => ({
        url: unarchiveBusinessTypePath(id),
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "BusinessType", id },
        { type: "BusinessType", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetBusinessTypesQuery,
  useCreateBusinessTypeMutation,
  useUpdateBusinessTypeMutation,
  useArchiveBusinessTypeMutation,
  useUnarchiveBusinessTypeMutation,
} = businessTypesSlice;

export { businessTypesSlice };
